import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface PaymentSuccessRateWidgetProps {
  successfulPayments: number;
  failedPayments: number;
  refundedPayments: number;
}

export function PaymentSuccessRateWidget({ 
  successfulPayments, 
  failedPayments, 
  refundedPayments 
}: PaymentSuccessRateWidgetProps) {
  const total = successfulPayments + failedPayments + refundedPayments;
  const successRate = total > 0 ? (successfulPayments / total) * 100 : 0;

  const data = [
    { name: 'Successful', value: successfulPayments, color: 'hsl(var(--chart-1))' },
    { name: 'Failed', value: failedPayments, color: 'hsl(var(--destructive))' },
    { name: 'Refunded', value: refundedPayments, color: 'hsl(var(--chart-4))' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Success Rate</CardTitle>
        <CardDescription>Overall payment completion metrics</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-4">
            <div className="text-center">
              <div className="text-4xl font-bold">{successRate.toFixed(1)}%</div>
              <div className="text-sm text-muted-foreground mt-1">Success Rate</div>
            </div>
            <div className="space-y-2">
              {data.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Transactions</span>
                <span className="font-bold">{total}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
